import React from "react";
import { Button, Tag, Typography, Row, Col, Statistic, theme } from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  DollarOutlined,
  CreditCardOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AppDrawer } from "../../Common/Layout";
import { DailyPracticeRecord, PracticeMetricStats } from "./types";
import { formatCurrencyString } from "./practiceUtils";

const { Text, Title } = Typography;

export interface PracticeQuickDrawerProps {
  open: boolean;
  onClose: () => void;
  record: DailyPracticeRecord | null;
  dateRangeText?: string;
}

interface StatTileProps {
  label: string;
  value: number;
}

const StatTile: React.FC<StatTileProps> = ({ label, value }) => {
  const { token } = theme.useToken();
  const isZero = !value || value === 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 10px",
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusSM,
        border: `1px solid ${token.colorBorderSecondary}`,
        gap: 6,
        minHeight: 32,
        boxSizing: "border-box",
      }}
    >
      <Text
        style={{
          fontSize: 11,
          color: token.colorTextSecondary,
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </Text>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: isZero ? token.colorTextQuaternary : token.colorText,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {isZero ? "0" : value.toLocaleString("en-GB")}
      </span>
    </div>
  );
};

interface FinancialRowProps {
  label: string;
  count: number;
  amount?: number;
  amountColor?: string;
}

const FinancialRow: React.FC<FinancialRowProps> = ({
  label,
  count,
  amount,
  amountColor = "blue",
}) => {
  const { token } = theme.useToken();
  const isZeroCount = !count || count === 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusSM,
        border: `1px solid ${token.colorBorderSecondary}`,
        gap: 8,
        minHeight: 34,
        boxSizing: "border-box",
      }}
    >
      <Text
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: token.colorTextSecondary,
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </Text>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: isZeroCount ? token.colorTextQuaternary : token.colorText,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {isZeroCount ? "0" : count.toLocaleString("en-GB")}
        </span>
        {amount !== undefined && (
          <Tag
            color={amount > 0 ? amountColor : "default"}
            bordered={false}
            style={{
              margin: 0,
              fontSize: 10,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              padding: "0 4px",
              borderRadius: 3,
              lineHeight: "18px",
            }}
          >
            {formatCurrencyString(amount)}
          </Tag>
        )}
      </div>
    </div>
  );
};

export const PracticeQuickDrawer: React.FC<PracticeQuickDrawerProps> = ({
  open,
  onClose,
  record,
  dateRangeText,
}) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  if (!record) return null;

  const stats: PracticeMetricStats = record.stats || {};
  const totalFilings =
    (stats.ct || 0) +
    (stats.vat || 0) +
    (stats.cis || 0) +
    (stats.sA100 || 0) +
    (stats.sA800 || 0) +
    (stats.sA900 || 0) +
    (stats.cs || 0) +
    (stats.cf || 0) +
    (stats.fps || 0) +
    (stats.eps || 0) +
    (stats.accountsReport || 0);

  const totalCollected =
    (stats.receiptsAmount || 0) +
    (stats.stripeTotalAmount || 0) +
    (stats.goCardlessTotalAmount || 0);

  const handleNavigateToDetails = () => {
    navigate(`/practices/${record.practiceId}`, {
      state: { practiceName: record.practiceName },
    });
    onClose();
  };

  return (
    <AppDrawer
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>{record.practiceName || `Practice #${record.practiceId}`}</span>
          <Tag
            color="blue"
            bordered={false}
            style={{
              borderRadius: 4,
              margin: 0,
              fontWeight: 500,
              fontSize: 11,
            }}
          >
            Tenant #{record.practiceId}
          </Tag>
        </div>
      }
      open={open}
      onClose={onClose}
      width={680}
      showCancel={true}
      cancelText="Close"
      extraFooter={
        <Button
          type="primary"
          icon={<SettingOutlined />}
          onClick={handleNavigateToDetails}
        >
          Manage Practice
        </Button>
      }
      bodyPadding="16px 20px"
      styles={{
        body: {
          backgroundColor: token.colorBgLayout,
        },
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: "12px 16px",
        }}
      >
        {/* Selected Activity Window Banner */}
        {dateRangeText && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusSM,
            }}
          >
            <CalendarOutlined
              style={{ color: token.colorPrimary, fontSize: 12 }}
            />
            <Text type="secondary" style={{ fontSize: 11, margin: 0 }}>
              Activity Window:{" "}
              <strong style={{ color: token.colorText }}>
                {dateRangeText}
              </strong>
            </Text>
          </div>
        )}

        {/* Executive KPI Summary Header */}
        <div
          style={{
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: "10px 16px",
          }}
        >
          <Row gutter={[12, 12]}>
            <Col span={8}>
              <Statistic
                title={
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: token.colorTextSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Total Filings
                  </span>
                }
                value={totalFilings}
                valueStyle={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: token.colorPrimary,
                  lineHeight: 1.2,
                }}
                prefix={
                  <FileTextOutlined style={{ fontSize: 14, marginRight: 4 }} />
                }
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: token.colorTextSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Invoices (Gross)
                  </span>
                }
                value={formatCurrencyString(stats.invoicesAmount)}
                valueStyle={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: token.colorSuccess,
                  lineHeight: 1.2,
                }}
                prefix={
                  <DollarOutlined style={{ fontSize: 14, marginRight: 4 }} />
                }
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: token.colorTextSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Collections
                  </span>
                }
                value={formatCurrencyString(totalCollected)}
                valueStyle={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: token.colorInfo,
                  lineHeight: 1.2,
                }}
                prefix={
                  <CreditCardOutlined
                    style={{ fontSize: 14, marginRight: 4 }}
                  />
                }
              />
            </Col>
          </Row>
        </div>

        {/* Section 1: Tax & Statutory Filings */}
        <div
          style={{
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: "10px 14px",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: token.colorTextSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 8,
            }}
          >
            Tax & Statutory Filings
          </div>
          <Row gutter={[8, 8]}>
            <Col span={8}>
              <StatTile label="VAT Returns" value={stats.vat || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="Corp. Tax (CT)" value={stats.ct || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="Accounts" value={stats.accountsReport || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="SA100" value={stats.sA100 || 0} />
            </Col>
            <Col span={8}>
              <StatTile
                label="SA800 / SA900"
                value={(stats.sA800 || 0) + (stats.sA900 || 0)}
              />
            </Col>
            <Col span={8}>
              <StatTile label="CIS" value={stats.cis || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="FPS (Payroll)" value={stats.fps || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="EPS (Payroll)" value={stats.eps || 0} />
            </Col>
            <Col span={8}>
              <StatTile
                label="CS / CF"
                value={(stats.cs || 0) + (stats.cf || 0)}
              />
            </Col>
          </Row>
        </div>

        {/* Section 2: Financial & Revenue Breakdown */}
        <div
          style={{
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: "10px 14px",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: token.colorTextSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 8,
            }}
          >
            Financial & Revenue Breakdown
          </div>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <FinancialRow
                label="Active Clients"
                count={stats.activeClients || 0}
              />
            </Col>
            <Col span={12}>
              <FinancialRow
                label="Invoices Raised"
                count={stats.invoicesCount || 0}
                amount={stats.invoicesAmount || 0}
                amountColor="green"
              />
            </Col>
            <Col span={12}>
              <FinancialRow
                label="Receipts Logged"
                count={stats.receiptsCount || 0}
                amount={stats.receiptsAmount || 0}
                amountColor="blue"
              />
            </Col>
            <Col span={12}>
              <FinancialRow
                label="Stripe Volume"
                count={stats.stripeCount || 0}
                amount={stats.stripeTotalAmount || 0}
                amountColor="purple"
              />
            </Col>
            <Col span={12}>
              <FinancialRow
                label="GoCardless Volume"
                count={stats.goCardlessCount || 0}
                amount={stats.goCardlessTotalAmount || 0}
                amountColor="cyan"
              />
            </Col>
            <Col span={12}>
              <FinancialRow
                label="Total Collections"
                count={
                  (stats.receiptsCount || 0) +
                  (stats.stripeCount || 0) +
                  (stats.goCardlessCount || 0)
                }
                amount={totalCollected}
                amountColor="gold"
              />
            </Col>
          </Row>
        </div>

        {/* Section 3: Communications & Operations */}
        <div
          style={{
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: "10px 14px",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: token.colorTextSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 8,
            }}
          >
            Communications & Operations
          </div>
          <Row gutter={[8, 8]}>
            <Col span={8}>
              <StatTile label="Emails Sent" value={stats.emails || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="Incoming Calls" value={stats.incoming || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="Outgoing Calls" value={stats.outgoing || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="Storage (MB)" value={stats.storageUses || 0} />
            </Col>
            <Col span={8}>
              <StatTile label="AML Checks" value={stats.aml || 0} />
            </Col>
            <Col span={8}>
              <StatTile
                label="AML Biometric"
                value={stats.amL_Biometric || 0}
              />
            </Col>
          </Row>
        </div>
      </div>
    </AppDrawer>
  );
};

export default PracticeQuickDrawer;
